import type { ReactElement, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { getRuntimeConfig } from "@/app/config/runtime-config";
import { AppFooter } from "@/app/layout/app-footer";
import { PublicHeader } from "@/app/layout/public-header";
import { isPasswordPolicyDescriptor } from "@/features/first-credential/first-credential-client";
import { ClmIcon } from "@/shared/components/icons/icon-map";
import { PublicBrandHeader } from "@/shared/components/public-brand-header";
import { SupportReference } from "@/shared/components/support-reference";
import { Alert, AlertDescription, AlertTitle, Button, Card } from "@/shared/components/ui";
import { cn } from "@/shared/utils/cn";
import { useHeldIdempotencyKey } from "@/shared/utils/use-held-idempotency-key";
import { isCredentialRecoveryReference } from "./credential-recovery-client";
import {
  FirstCredentialFollowUp,
  RECONCILIATION_REQUIRED_HEADING,
  RECOVERY_DIRECTED_GUIDANCE,
  SIGN_IN_TO_CONTINUE_LABEL,
} from "./first-credential-follow-up";
import {
  clearFirstCredentialProof,
  holdFirstCredentialProof,
  readFirstCredentialProof,
  type FirstCredentialProofOwner,
} from "./first-credential-proof-holder";
import {
  captureInvitationProofFromLocation,
  claimCapturedInvitationProof,
  clearCapturedInvitationProof,
  type InvitationProofClaimTicket,
} from "./invitation-proof-fragment";
import { RegistrationForm } from "./registration-form";
import {
  RegistrationFinancialOutcome,
  type FinancialHandoffDescriptor,
} from "./registration-financial-outcome";
import {
  acceptCompletionFinancialHandoff,
  clearRegistrationFinancialTokenByOwner,
  type RegistrationFinancialTokenOwner,
} from "./registration-financial-token-holder";
import {
  type CompletionSucceeded,
  type InvitationLookupReady,
  isMemberCompletionState,
  type MemberFinalizeCompletionRequest,
  readOwnMemberCompletionState,
  readOwnRequiredFields,
  RegistrationInvitationApiError,
} from "./registration-invitation-client";
import { useCredentialRecoveryClient } from "./use-credential-recovery";
import {
  createCompletionFingerprint,
  type PasswordFreeCompletionRequest,
  useInvitationCompletion,
  useInvitationLookup,
  useRegistrationInvitationCachePurge,
} from "./use-registration-invitation";
import {
  BoundCompanyPanel,
  VariantContextPanel,
  buildVariantCompletionRequest,
  compileVariantRenderPlan,
  isSupportedRegistrationVariant,
  renderedFieldPaths,
  type VariantFormValues,
} from "./variant-fields";

/**
 * Public invitation registration page (CLMS-113 T036 US1, T068/T069 US3;
 * variant set narrowed to the two supported registration variants by
 * 011-20). Orchestrates the UI-contract state machine: fragment capture on
 * first execution, lookup, ready/editing/submitting for the two supported
 * registration variants, and the generic terminal/support states. The proof
 * lives only in the ownership-scoped module holder — never in React state;
 * terminal states clear the proof (with this page's own token), the
 * idempotency key, and all sensitive prefill/form state. No authenticated
 * navigation elements render on this route.
 */

const VARIANT_HEADINGS: Record<InvitationLookupReady["registrationVariant"], string> = {
  direct_fellow: "Complete your Fellow registration",
  member: "Complete your Member registration",
};

// 011-15 UI contract L172: a `not_available` refusal (or any start failure)
// renders the same guidance unchanged; this polite announcement only restates
// it for assistive tech — derived from the rendered label and guidance so the
// announcement can never drift from the visible copy.
const RECOVERY_GUIDANCE_ANNOUNCEMENT = `${SIGN_IN_TO_CONTINUE_LABEL}. ${RECOVERY_DIRECTED_GUIDANCE}`;

// Completion-phase `reconciliation_required` Support message (011-15 FR-050
// outcomes table); the cold `support_required` terminal renders it verbatim.
const RECONCILIATION_REQUIRED_MESSAGE =
  "Our team needs to review this registration before it can be completed.";

// Completed member whose response lacked the contract-required financial
// handoff (clm-web#242 item 6): the registration itself WAS saved, so the
// terminal must say so truthfully — not "link unavailable" (untrue) and not
// "Registration complete" with a payment step that cannot mount. The member
// also holds a warm credential and the dues invoice follows by email
// regardless (FR-049), so the copy says how to proceed, not just "contact
// support" (DES-1); the terminal carries the same "Sign in" entry as the
// completed panel.
const FINANCIAL_HANDOFF_UNAVAILABLE_HEADING = "Your registration was saved";
const FINANCIAL_HANDOFF_UNAVAILABLE_MESSAGE =
  "Your registration was saved, but we couldn't load the next step to set up your membership dues. Your dues invoice will be emailed to you with payment instructions, and you can sign in now with the password you just created.";

// Single source for the "form diverged from the failed submission" guidance
// (uncertain-outcome Alert and SupportPanel). A member edit moves the form to
// a new key, which re-opens the credential group (FR-050 L156), so the member
// variant also says the password must be typed again.
const CHANGED_DETAILS_MESSAGE =
  "You changed your details. Choose Complete registration below to submit them.";
const CHANGED_DETAILS_MEMBER_MESSAGE =
  "You changed your details. Re-enter your password below, then choose Complete registration to submit them.";

function changedDetailsMessage(isMember: boolean): string {
  return isMember ? CHANGED_DETAILS_MEMBER_MESSAGE : CHANGED_DETAILS_MESSAGE;
}

// CLMS-268 T042 (FR-014): the completion was refused 409 DOMAIN_LEGACY_OWNED
// while the legacy application owns registrations. Invitee-facing copy only:
// plain, no apology, no system vocabulary (never "legacy", "domain" or the
// staff pointer), no retry affordance — CLM staff follow up. The 409 only
// guarantees the refused request changed nothing; the panel can also follow a
// refused retry after an uncertain completion (or a member `finalize`
// replaying an already-recorded initial submission), so earlier details may
// have been saved and the copy makes no claim about whether they were.
const REGISTRATION_PAUSED_HEADING = "Registration is paused";
const REGISTRATION_PAUSED_MESSAGE =
  "We can't complete your registration right now. CLM staff will contact you to finish it. You don't need to do anything else.";

// 429 guidance. When the limited request was the member `initial`, the
// credential group re-opened and the password was cleared, so the copy must
// say so instead of claiming nothing was lost.
const RATE_LIMITED_MESSAGE =
  "Please wait a moment, then complete your registration again. Your information was not lost.";
const RATE_LIMITED_REENTER_PASSWORD_MESSAGE =
  "Please wait a moment, then re-enter your password below and choose Complete registration to try again. Your other details were kept.";

interface TerminalState {
  /** `recovery-directed` (011-21 addendum L60 / clm-web#238 V5): a cold
   * member `ready` whose `memberCompletionState` is `recovery_available`.
   * Renders the cold `no_action` recovery row verbatim (no Editing, no
   * password control, prefill discarded) WITHOUT a status call — the lookup
   * already resolved the disposition — and keeps the proof held for the
   * A §8.1 "Sign in to continue" entry. */
  kind:
    | "completed"
    | "expired"
    | "already-completed"
    | "recovery-directed"
    | "registration-paused"
    | "unavailable"
    | "support";
  correlationId?: string;
  nextAction?: CompletionSucceeded["nextAction"];
  /** Resolved variant of a `completed` outcome. Member completions (FR-050)
   * render the warm sign-in guidance — the member just created their
   * password, so no status/credential step ever follows. */
  registrationVariant?: CompletionSucceeded["registrationVariant"];
  /** Member-variant context for the already-completed follow-up surface
   * (011-21 FR-050 addendum): a Member's `set_password_available` status is
   * treated as `no_action` — never a credential CTA. */
  memberContext?: boolean;
  /** Support-state heading override (completion-phase
   * `reconciliation_required`: "We couldn't finish setting up your account"). */
  heading?: string;
  /** Non-secret latch keeping the CLMS-137 first-credential follow-up
   * surface mounted on `sign_in`/already-completed surfaces. The
   * registration proof itself is NEVER copied into this immutable state
   * object (React's double-buffered fibers would retain it in the
   * `alternate` tree after a scrub); it lives only in the mutable
   * first-credential-proof-holder module slot, cleared synchronously once
   * redemption can never run while the rendered guidance stays. */
  firstCredentialFollowUp?: boolean;
  /** Member pending-financial-setup handoff context (CLMS-117 T033 owns the
   * status-driven result surface below the completion panel). Only the
   * Member success schema carries it (discriminated success union); the
   * descriptor carries ONLY non-secret fields (status/correlationId plus the
   * optional CLMS-117 `handoffId` that activates status polling). The FR-046
   * `accessToken` is stripped at the completion boundary and parked in the
   * module-memory token holder — never in this immutable state object. */
  financialHandoff?: FinancialHandoffDescriptor;
  /** Non-secret latch, set at completion acceptance: the handoff carried
   * BOTH the handoffId and an access token (deposited into the holder), so
   * the inline financial flow below renders with payment-step authority —
   * the completion copy points at it instead of the deferred "continues
   * separately" guidance. Latched here (not re-derived from the holder)
   * so a later token scrub cannot flip the copy mid-view. */
  inlineFinancialAuthority?: boolean;
  message?: string;
  /** Support-state "Sign in" entry: only the completed-member
   * missing-financialHandoff terminal sets it — the member holds a warm
   * credential there. Lookup-phase and reconciliation support terminals
   * never carry it. */
  signInEntry?: boolean;
}

/**
 * The last dispatched logical submission. Never holds the password (UI
 * contract L157); `key` is retained so `finalize` retries reuse it verbatim
 * even if the held-key holder is later reset.
 */
interface LastSubmission {
  request: PasswordFreeCompletionRequest;
  fingerprint: string;
  key: string;
  /** True once an `initial` for this key has been handed to the network (member only). */
  initialDispatched: boolean;
  /** The completion mode of the LAST dispatch under this key (member only). */
  mode: "initial" | "finalize";
  /**
   * False once the server answered the member `initial` with an outcome that
   * cannot have recorded a durable attempt: `rate_limited` (the limiter runs
   * before the controller) or `dependency_unavailable` (the 503 envelope is
   * identical before and after the `prepared` row exists, so the SPA fails
   * toward re-entry — a same-key `initial` is accepted in every server state).
   */
  initialRecorded: boolean;
}

/**
 * True when the member `initial` under the held key is NOT known to be
 * durably recorded: the next submit must carry the password again (no
 * password-free `finalize`, no timed re-send, credential group re-opened).
 * Evaluated against the live outcome as well as the ref so the very render
 * that first shows the outcome already reflects it.
 */
function memberInitialUnrecorded(
  last: LastSubmission | null,
  result: { outcome: string } | undefined,
): boolean {
  if (last === null || last.request.registrationVariant !== "member" || last.mode !== "initial") {
    return false;
  }
  if (!last.initialRecorded) {
    return true;
  }
  return result?.outcome === "rate_limited" || result?.outcome === "dependency_unavailable";
}

/** Support-state latch: keeps the support panel mounted while a retry is in flight (U6). */
interface SupportContext {
  correlationId?: string;
  message: string;
}

/** Rate-limit latch: keeps the wait-and-retry panel mounted while a retry is in flight. */
interface RateLimitContext {
  correlationId?: string;
}

export function RegistrationInvitationPage(): ReactElement {
  // The proof NEVER enters React state (or any fiber's hook state): React
  // double-buffers fibers, so a secret in useState would survive in the
  // alternate tree after a scrub. Fragment capture happens during first
  // render, before any network call, but the RENDER PHASE must not touch
  // the ownership-scoped module holder (V18): React can start and abandon
  // a render without committing, and an abandoned render that parked the
  // secret would leak it into the holder with no cleanup AND steal the
  // ownership generation from a legitimately committed flow. The capture
  // therefore lands in a mutable fiber-local ref during the lazy
  // initializer — an abandoned render's ref object is simply
  // garbage-collected with no global side effect — and is parked into the
  // holder only on COMMIT (the effect below). Only the non-secret
  // ownership token (ref) and a hasProof render latch remain in the
  // component. The capture helper is one-shot memoized, so StrictMode
  // double-invoking this initializer cannot lose the proof.
  const proofOwnerRef = useRef<FirstCredentialProofOwner | null>(null);
  const capturedProofRef = useRef<string | null>(null);
  // Per-mount claim capability: minted by the capture this mount performs
  // and required to re-read or park the staged proof. StrictMode's
  // double-invoked initializer shares this ref (same fiber), so the second
  // invocation presents the first invocation's ticket; an unrelated mount
  // has no ticket and can never consume a capture staged by an abandoned
  // render.
  const claimTicketRef = useRef<InvitationProofClaimTicket | null>(null);
  const parkedOnceRef = useRef(false);
  // FR-046: owner token for the completion-deposited financial access token
  // — the capability for this page's route-unmount scrub of that secret.
  const financialTokenOwnerRef = useRef<RegistrationFinancialTokenOwner | null>(null);
  const [hasProof, setHasProof] = useState<boolean>(() => {
    const capture = captureInvitationProofFromLocation(claimTicketRef.current);
    if (capture.ticket !== null) {
      claimTicketRef.current = capture.ticket;
    }
    capturedProofRef.current = capture.proof;
    return capture.proof !== null;
  });
  const [terminalState, setTerminalState] = useState<TerminalState | null>(null);
  const [lookupSupportCtx, setLookupSupportCtx] = useState<SupportContext | null>(null);
  const [completionSupportCtx, setCompletionSupportCtx] = useState<SupportContext | null>(null);
  const [lookupRateLimitCtx, setLookupRateLimitCtx] = useState<RateLimitContext | null>(null);
  const [lookupRetryHeld, setLookupRetryHeld] = useState(false);
  const [completionSubmitHeld, setCompletionSubmitHeld] = useState(false);
  // 011-15 L156/L209: true while the live form's canonical fingerprint
  // differs from the last dispatched submission's. A dirty form has no
  // same-key retry (`Try again` is withdrawn) and its credential is no longer
  // "on file" — the next submit is a validated NEW `initial` under a NEW key.
  const [formDirty, setFormDirty] = useState(false);

  const lookup = useInvitationLookup();
  const completion = useInvitationCompletion();
  const idempotencyKeys = useHeldIdempotencyKey();
  const purgeMutationCache = useRegistrationInvitationCachePurge();
  const recoveryClient = useCredentialRecoveryClient();
  const [recoveryStarting, setRecoveryStarting] = useState(false);
  const [recoveryAnnouncement, setRecoveryAnnouncement] = useState<string | null>(null);
  // Guards async continuations (recovery start) against a mount that has
  // already gone away — no state writes after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const lastSubmissionRef = useRef<LastSubmission | null>(null);
  // One-shot password handoff for a member `initial` (UI contract L153/L157):
  // written by handleSubmit, consumed (and nulled) by the mutation at dispatch.
  const pendingPasswordRef = useRef<string | null>(null);
  // Latched by a `resubmit_required` outcome: the next unchanged-form submit
  // is a NEW `initial` under the same key (L155), not a `finalize`.
  const resubmitRequiredRef = useRef(false);
  const lookupStartedRef = useRef(false);
  const proofScrubTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const validationAlertRef = useRef<HTMLDivElement | null>(null);
  const resubmitAlertRef = useRef<HTMLDivElement | null>(null);
  const uncertainAlertRef = useRef<HTMLDivElement | null>(null);
  const rateLimitAlertRef = useRef<HTMLDivElement | null>(null);

  const { mutate: lookupMutate, reset: lookupReset } = lookup;
  const { mutate: completionMutate, reset: completionReset } = completion;
  const { reset: resetIdempotencyKeys } = idempotencyKeys;

  // Ownership-gated read of this page's held proof (null once scrubbed or
  // if a newer flow took ownership).
  const readProof = useCallback(
    (): string | null => readFirstCredentialProof(proofOwnerRef.current),
    [],
  );

  const enterTerminal = useCallback(
    (state: TerminalState): void => {
      // Terminal: scrub the proof, the held idempotency key, the pending
      // submission, and the lookup prefill from memory — including the
      // mutation-cache entries whose variables hold the raw proof. The
      // proof lives only in the ownership-scoped holder: follow-up
      // terminals (and the recovery-directed cold-ready terminal, whose
      // sign-in entry posts the proof) leave it held there; every other
      // terminal scrubs it with this page's own token.
      if (!state.firstCredentialFollowUp && state.kind !== "recovery-directed") {
        clearFirstCredentialProof(proofOwnerRef.current);
      }
      setTerminalState(state);
      // Ticket-scoped: scrubs only THIS flow's staged capture, never one a
      // replacing flow staged in the same commit.
      clearCapturedInvitationProof(claimTicketRef.current);
      resetIdempotencyKeys();
      lastSubmissionRef.current = null;
      setFormDirty(false);
      // 011-15 L158: leaving Editing for a terminal state clears the password
      // together with the proof — including one an offline-paused mutation
      // has not yet taken.
      pendingPasswordRef.current = null;
      lookupReset();
      completionReset();
      purgeMutationCache();
    },
    [resetIdempotencyKeys, lookupReset, completionReset, purgeMutationCache],
  );

  // Commit-time parking (V18): the captured proof enters the module holder
  // only when this render actually COMMITS, so an abandoned render can
  // never leave a secret in the holder or steal a committed flow's
  // ownership. StrictMode's simulated remount runs setup → cleanup → setup:
  // the cleanup copies the still-owned proof back into the capture ref
  // WITHOUT clearing the holder, so the second setup re-parks it (a re-hold
  // just advances ownership, and the pending deferred-scrub timer's stale
  // token becomes a no-op). A real unmount leaves the final scrub to the
  // deferred timer in the effect below; a terminal state that already
  // scrubbed the proof reads back null, so a scrubbed secret is never
  // resurrected. This effect is defined FIRST so the lookup effect below
  // reads an already-parked proof.
  useEffect(() => {
    const captured = capturedProofRef.current;
    if (captured !== null) {
      capturedProofRef.current = null;
      // First park only: the value is a RENDER-PHASE copy, and a retained
      // concurrent render could commit long after capture — re-enforce the
      // abandoned-render deadline at commit before it can reach the holder.
      // Re-parks (StrictMode cleanup copies the value back from the OWNED
      // holder in the same commit cycle) are exempt: their value never left
      // owned custody.
      if (parkedOnceRef.current || claimCapturedInvitationProof(claimTicketRef.current, captured)) {
        parkedOnceRef.current = true;
        proofOwnerRef.current = holdFirstCredentialProof(captured);
      } else {
        // Fail closed: the render-phase copy expired before commit, so the
        // proof never reaches the holder — drop the render latch too so the
        // page shows the generic unavailable panel instead of resolving
        // forever against a lookup that will never start.
        setHasProof(false);
      }
    }
    return () => {
      capturedProofRef.current = readFirstCredentialProof(proofOwnerRef.current);
    };
  }, []);

  // Unmount mid-flow (U2): the same scrub as a terminal state so no proof,
  // prefill, or completion request outlives the route in mutation caches —
  // or in the ownership-scoped proof holder. The holder scrub is deferred
  // one cancelable macrotask and gated on THIS page's own token: a real
  // unmount scrubs it, while StrictMode's synchronous simulated remount
  // cancels it in the paired setup below (which also re-arms
  // lookupStartedRef so the lookup restarts after this cleanup), and a
  // newer flow's proof can never be wiped by a stale token.
  useEffect(() => {
    if (proofScrubTimerRef.current !== null) {
      clearTimeout(proofScrubTimerRef.current);
      proofScrubTimerRef.current = null;
    }
    return () => {
      const owner = proofOwnerRef.current;
      const financialTokenOwner = financialTokenOwnerRef.current;
      proofScrubTimerRef.current = setTimeout(() => {
        clearFirstCredentialProof(owner);
        // Real unmount: also scrub the commit-parking capture ref's copy
        // (restored by the parking cleanup above for StrictMode re-parking).
        capturedProofRef.current = null;
        // FR-046: the handoff-scoped access token must not outlive the
        // route. Owner-scoped, so a newer flow's deposit survives this
        // stale timer; StrictMode's simulated remount cancels it above.
        clearRegistrationFinancialTokenByOwner(financialTokenOwner);
      }, 0);
      // Ticket-scoped (V10): a replaced flow's unmount cleanup can fire
      // AFTER a newer flow staged its own capture in the same commit — the
      // stale ticket makes this a no-op instead of erasing that capture.
      clearCapturedInvitationProof(claimTicketRef.current);
      resetIdempotencyKeys();
      lastSubmissionRef.current = null;
      // A disposed route must never let a paused/late mutation read the
      // credential (011-15 L154): revoke the handoff with the route.
      pendingPasswordRef.current = null;
      lookupStartedRef.current = false;
      lookupReset();
      completionReset();
      purgeMutationCache();
    };
  }, [resetIdempotencyKeys, lookupReset, completionReset, purgeMutationCache]);

  // Kick off lookup exactly once; the module-level capture is scrubbed here
  // because the proof now lives solely in the ownership-scoped holder.
  useEffect(() => {
    if (lookupStartedRef.current) {
      return;
    }
    lookupStartedRef.current = true;
    clearCapturedInvitationProof(claimTicketRef.current);
    const proof = readProof();
    if (proof) {
      lookupMutate(proof);
    }
  }, [readProof, lookupMutate]);

  // Lookup outcomes that leave the editing flow. Only the two supported
  // registration variants (011-20) enter the editing state: a ready lookup
  // carrying a retired or unknown variant value is a contract violation and
  // fails closed into the generic unavailable terminal — never a partial
  // form. rate_limited is NON-terminal (US4): the proof is kept so the user
  // can retry once the abuse window passes.
  useEffect(() => {
    const result = lookup.data;
    if (
      !result ||
      result.outcome === "dependency_unavailable" ||
      result.outcome === "rate_limited"
    ) {
      return;
    }
    if (result.outcome === "ready") {
      if (!isSupportedRegistrationVariant(result.registrationVariant)) {
        enterTerminal({ kind: "unavailable", correlationId: result.correlationId });
        return;
      }
      // 011-21 addendum L60 (clm-web#238 V5): a cold member `ready` routes on
      // the server's `memberCompletionState` disposition BEFORE Editing.
      // `none`/absent and `resubmittable` enter Editing (first submit is
      // `initial`); `recovery_available` renders the cold `no_action`
      // recovery row without a status call; `support_required` renders the
      // Support surface byte-identical to the warm completion-phase
      // `reconciliation_required` terminal (UI contract 17a). A present but
      // unrecognised state is owned by the fail-closed effect below (never
      // Editing). The discriminator itself never reaches the DOM, storage,
      // or logs. Read as an OWN property only: an inherited value is absent.
      if (result.registrationVariant === "member") {
        const memberCompletionState = readOwnMemberCompletionState(result);
        if (memberCompletionState === "recovery_available") {
          enterTerminal({ kind: "recovery-directed", correlationId: result.correlationId });
        } else if (memberCompletionState === "support_required") {
          enterTerminal({
            kind: "support",
            heading: RECONCILIATION_REQUIRED_HEADING,
            correlationId: result.correlationId,
            message: RECONCILIATION_REQUIRED_MESSAGE,
          });
        }
      }
      return;
    }
    // Already-completed re-entry keeps the durable-notice recovery path
    // non-minting: the follow-up latch is set only while the proof is still
    // held (it stays in the holder — never copied into TerminalState).
    enterTerminal(
      mapPublicOutcomeToTerminal(
        result,
        result.outcome === "already_completed" && readProof() !== null,
      ),
    );
  }, [lookup.data, readProof, enterTerminal]);

  // Support-state latches (U6): remember the dependency-unavailable context
  // so the support panel stays mounted — with a single focus move — while a
  // retry is in flight, instead of flipping through resolving/editing.
  useEffect(() => {
    const result = lookup.data;
    if (result?.outcome === "dependency_unavailable") {
      setLookupSupportCtx({ correlationId: result.correlationId, message: result.message });
    } else if (result) {
      setLookupSupportCtx(null);
    }
  }, [lookup.data]);

  // Rate-limit latch (US4): mirrors the support latch so the wait-and-retry
  // panel stays mounted while a retry is pending. The Retry-After hint from
  // the 429 holds the retry action for the advertised window.
  useEffect(() => {
    const result = lookup.data;
    if (result?.outcome === "rate_limited") {
      setLookupRateLimitCtx({ correlationId: result.correlationId });
      const retryAfterSeconds = result.retryAfterSeconds ?? 0;
      if (retryAfterSeconds > 0) {
        setLookupRetryHeld(true);
        const timer = setTimeout(() => {
          setLookupRetryHeld(false);
        }, retryAfterSeconds * 1000);
        return () => {
          clearTimeout(timer);
        };
      }
      setLookupRetryHeld(false);
    } else if (result) {
      setLookupRateLimitCtx(null);
      setLookupRetryHeld(false);
    }
    return undefined;
  }, [lookup.data]);

  useEffect(() => {
    const result = completion.data;
    if (result?.outcome === "dependency_unavailable") {
      setCompletionSupportCtx({
        correlationId: result.correlationId,
        message: `${result.message} Your information was not lost.`,
      });
    } else if (result) {
      setCompletionSupportCtx(null);
    }
  }, [completion.data]);

  // Completion Retry-After hold (US4): mirrors the lookup hold. A rate
  // limited completion keeps the form, proof, and idempotency key intact
  // but disables submission for the advertised window before re-enabling.
  useEffect(() => {
    const result = completion.data;
    if (result?.outcome === "rate_limited") {
      const retryAfterSeconds = result.retryAfterSeconds ?? 0;
      if (retryAfterSeconds > 0) {
        setCompletionSubmitHeld(true);
        const timer = setTimeout(() => {
          setCompletionSubmitHeld(false);
        }, retryAfterSeconds * 1000);
        return () => {
          clearTimeout(timer);
        };
      }
      setCompletionSubmitHeld(false);
    } else if (result) {
      setCompletionSubmitHeld(false);
    }
    return undefined;
  }, [completion.data]);

  useEffect(() => {
    // A thrown (uncertain) outcome leaves support mode: the uncertain-outcome
    // alert owns the retry action from here.
    if (completion.isError) {
      setCompletionSupportCtx(null);
    }
  }, [completion.isError]);

  // FR-050 request discipline reacting to server outcomes:
  // - `resubmit_required` (L155): the pending credential is gone; the next
  //   submit of the unchanged form is a NEW `initial` under the SAME key.
  // - `validation_failed` (L150): a 422 creates no durable attempt, so the
  //   corrected form is a NEW `initial` under a NEW key even when the only
  //   change is the password (which is excluded from key derivation, L157).
  // - `rate_limited` / `dependency_unavailable` to a member `initial`: the
  //   429 is answered by the limiter before the controller and the 503 may
  //   precede the `prepared` row, so nothing durable is known to exist. The
  //   next submit under the same key is an `initial` WITH the password again.
  useEffect(() => {
    const outcome = completion.data?.outcome;
    if (outcome === "resubmit_required") {
      resubmitRequiredRef.current = true;
    } else if (outcome === "validation_failed") {
      resubmitRequiredRef.current = false;
      lastSubmissionRef.current = null;
      setFormDirty(false);
      resetIdempotencyKeys();
    } else if (outcome === "rate_limited" || outcome === "dependency_unavailable") {
      const last = lastSubmissionRef.current;
      if (
        last !== null &&
        last.request.registrationVariant === "member" &&
        last.mode === "initial"
      ) {
        lastSubmissionRef.current = { ...last, initialRecorded: false };
      }
    }
  }, [completion.data, resetIdempotencyKeys]);

  // L168: a member `finalize` answered 503 dependency_unavailable WITH
  // Retry-After stays pending ("Still finishing your registration…") and the
  // SPA re-sends `finalize` under the same key once the interval elapses.
  // Without Retry-After the support surface (with manual Try again) owns it.
  // The same 503 envelope answering the password-bearing `initial` is NOT
  // finalize-eligible: the attempt may never have been recorded, so the member
  // re-enters the password instead (see `memberInitialUnrecorded`).
  const retryLastSubmissionRef = useRef<() => void>(() => undefined);
  const finalizeRetryAfterSeconds = useMemo((): number => {
    const result = completion.data;
    if (result?.outcome !== "dependency_unavailable") {
      return 0;
    }
    const last = lastSubmissionRef.current;
    if (
      last === null ||
      last.request.registrationVariant !== "member" ||
      last.mode !== "finalize"
    ) {
      return 0;
    }
    return result.retryAfterSeconds ?? 0;
  }, [completion.data]);
  // Latch: TanStack clears `completion.data` the moment the timed re-send
  // dispatches, so the Retry-After derivation above drops to 0 while the
  // re-sent `finalize` is in flight. The latch keeps the pending surface
  // ("Still finishing your registration…") and the `pending:finalize` phase
  // stable across that gap — no SupportPanel flash, no second h1 focus move.
  // It is armed on a 503-with-Retry-After and released only once the
  // mutation settles on anything else (another 503 re-arms it).
  const [finalizeRetryLatched, setFinalizeRetryLatched] = useState(false);
  useEffect(() => {
    if (finalizeRetryAfterSeconds > 0) {
      setFinalizeRetryLatched(true);
    } else if (!completion.isPending) {
      setFinalizeRetryLatched(false);
    }
  }, [finalizeRetryAfterSeconds, completion.isPending]);
  const finalizeRetryPending =
    finalizeRetryAfterSeconds > 0 || (finalizeRetryLatched && completion.isPending);
  useEffect(() => {
    if (finalizeRetryAfterSeconds <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      retryLastSubmissionRef.current();
    }, finalizeRetryAfterSeconds * 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [finalizeRetryAfterSeconds, completion.data]);

  // Unexpected lookup failure: same generic unavailable state, no detail.
  // A fail-closed ready payload still carries the service's correlationId, so
  // the terminal can offer a support reference without surfacing why.
  useEffect(() => {
    if (lookup.isError) {
      const correlationId =
        lookup.error instanceof RegistrationInvitationApiError
          ? lookup.error.correlationId
          : undefined;
      enterTerminal({ kind: "unavailable", correlationId });
    }
  }, [lookup.isError, lookup.error, enterTerminal]);

  // Completion outcomes that leave the editing flow. rate_limited stays in
  // the editing state (U7): it is temporary, so the form, proof, and held
  // idempotency key are all preserved for the user's next attempt.
  // resubmit_required (FR-050) also stays in Editing: the initial password
  // was already cleared at hand-over, so the member re-enters it and submits
  // again — same held key when the canonical form is unchanged.
  useEffect(() => {
    const result = completion.data;
    if (
      !result ||
      result.outcome === "validation_failed" ||
      result.outcome === "dependency_unavailable" ||
      result.outcome === "rate_limited" ||
      result.outcome === "resubmit_required"
    ) {
      return;
    }
    if (result.outcome === "completed") {
      // sign_in completions resolve their follow-up action through the
      // non-minting first-credential status operation (CLMS-137). The proof
      // is already parked in the ownership-scoped holder — never in
      // TerminalState — so follow-up eligibility just checks it is held.
      // Member completions never latch it: the credential was created in
      // this same flow (FR-050) and the Member schema's nextAction is
      // `continue_financial_setup`, so the warm guidance is static.
      const firstCredentialFollowUp = result.nextAction === "sign_in" && readProof() !== null;
      // Discriminated success union: only the Member schema carries the
      // financial handoff; Fellow schemas reject it as an unknown property.
      // FR-046 boundary: the completion response may additively carry the
      // secret accessToken — accept() deposits it into the module-memory
      // holder and returns a fresh descriptor with ONLY non-secret fields,
      // so the raw response object never enters React state. The deposit's
      // owner token is kept for this page's route-unmount scrub.
      let financialHandoff: FinancialHandoffDescriptor | undefined;
      let inlineFinancialAuthority = false;
      if (result.registrationVariant === "member") {
        // Fail closed (clm-web#242 item 6): the completion client admits any
        // `completed` body, so the contract-required handoff object may be
        // absent at runtime. Without it there is nothing to accept or poll —
        // but the registration was saved, so surface a truthful support
        // terminal with the reference, never a dereference and never the
        // "link unavailable" copy.
        if (
          result.financialHandoff === null ||
          typeof result.financialHandoff !== "object" ||
          Array.isArray(result.financialHandoff)
        ) {
          enterTerminal({
            kind: "support",
            heading: FINANCIAL_HANDOFF_UNAVAILABLE_HEADING,
            message: FINANCIAL_HANDOFF_UNAVAILABLE_MESSAGE,
            correlationId: result.correlationId,
            signInEntry: true,
          });
          return;
        }
        const accepted = acceptCompletionFinancialHandoff(result.financialHandoff);
        financialTokenOwnerRef.current = accepted.tokenOwner;
        financialHandoff = accepted.descriptor;
        // Token deposited (implies handoffId): the inline flow will mount
        // with payment-step authority in this same commit.
        inlineFinancialAuthority = accepted.tokenOwner !== null;
      }
      enterTerminal({
        kind: "completed",
        nextAction: result.nextAction,
        registrationVariant: result.registrationVariant,
        ...(financialHandoff !== undefined && { financialHandoff }),
        ...(inlineFinancialAuthority && { inlineFinancialAuthority: true }),
        ...(firstCredentialFollowUp && { firstCredentialFollowUp: true }),
        correlationId: result.correlationId,
      });
      return;
    }
    if (result.outcome === "domain_legacy_owned") {
      // CLMS-268 T042 (FR-014): a deliberate refusal, not an uncertain
      // outcome — the page's own terminal (enterTerminal scrubs the proof,
      // held key, pending password, prefill and mutation cache). Never the
      // legacy pointer, never retried.
      enterTerminal({ kind: "registration-paused" });
      return;
    }
    if (result.outcome === "reconciliation_required") {
      // Completion-phase reconciliation (011-15 FR-050 outcomes table):
      // contract heading, no retry, no navigation — support only.
      enterTerminal({
        kind: "support",
        heading: RECONCILIATION_REQUIRED_HEADING,
        correlationId: result.correlationId,
        message: RECONCILIATION_REQUIRED_MESSAGE,
      });
      return;
    }
    const memberContext =
      lookup.data?.outcome === "ready" && lookup.data.registrationVariant === "member";
    enterTerminal({
      ...mapPublicOutcomeToTerminal(
        result,
        result.outcome === "already_completed" && readProof() !== null,
      ),
      ...(memberContext && { memberContext: true }),
    });
  }, [completion.data, lookup.data, readProof, enterTerminal]);

  const lookupReady =
    lookup.data?.outcome === "ready" &&
    isSupportedRegistrationVariant(lookup.data.registrationVariant)
      ? lookup.data
      : undefined;

  // Compiled ∩ server field intersection for the resolved variant (T068),
  // with the variant-scoped closed option catalogs resolved per field and
  // the FR-028 `requiredFields` coverage declaration (Member only; T103).
  // `requiredFields` goes through the own-property helper: an inherited or
  // malformed value counts as absent (legacy optional rendering).
  const renderPlan = useMemo(
    () =>
      lookupReady
        ? compileVariantRenderPlan(
            lookupReady.registrationVariant,
            lookupReady.editableFields,
            lookupReady.optionCatalogs,
            lookupReady.registrationVariant === "member"
              ? readOwnRequiredFields(lookupReady)
              : undefined,
          )
        : undefined,
    [lookupReady],
  );

  // UI contract: an absent/empty required catalog for any server-editable
  // reference field fails closed into the generic unavailable state — never
  // a partially usable form.
  const catalogsMissing = renderPlan !== undefined && renderPlan.missingCatalogPaths.size > 0;

  // FR-050 / UI contract L149: the member credential group renders the
  // ready lookup's `passwordPolicy`. Without a usable descriptor the group
  // would be hidden and an EMPTY password submitted, so a member `ready`
  // whose own `passwordPolicy` fails the descriptor guard fails closed the
  // same way as a missing catalog (L85: never a partially usable form). The
  // HTTP client rejects this payload at the boundary already; this covers
  // every other RegistrationInvitationClient implementation.
  const memberPolicyMissing =
    lookupReady?.registrationVariant === "member" &&
    !(
      Object.hasOwn(lookupReady, "passwordPolicy") &&
      isPasswordPolicyDescriptor(lookupReady.passwordPolicy)
    );
  // clm-web#238 V5 / UI contract 17a: the cold member routing treats only
  // ABSENCE of `memberCompletionState` as `none`. A present value outside the
  // contract enum would otherwise fall through to Editing exactly like
  // `none` (a renamed `recovery_available` would mint a duplicate credential
  // attempt), so it fails closed like a missing catalog. The HTTP client
  // rejects this payload at the boundary already; this covers every other
  // RegistrationInvitationClient implementation. Every read below goes
  // through the own-property helper, so an inherited value counts as absent.
  const memberCompletionState =
    lookupReady?.registrationVariant === "member"
      ? readOwnMemberCompletionState(lookupReady)
      : undefined;
  const memberCompletionStateInvalid =
    memberCompletionState !== undefined && !isMemberCompletionState(memberCompletionState);
  const readyPayloadUnusable =
    catalogsMissing || memberPolicyMissing || memberCompletionStateInvalid;

  // 011-21 addendum L60: a cold member `ready` whose disposition routes away
  // from Editing (`recovery_available` / `support_required`) is committed to
  // TerminalState by the lookup-outcome effect — one render later. That
  // frame must never mount the form (no prefill in the DOM, no password
  // control, no stray heading focus), so it holds a quiet transitional phase.
  const memberColdRoutingBound =
    memberCompletionState === "recovery_available" || memberCompletionState === "support_required";

  // Missing catalog / unusable policy is a TERMINAL transition, so it must
  // run the same scrub as every other terminal: enterTerminal clears the
  // in-memory proof, the idempotency keys, and the mutation-cache entries
  // holding the raw proof and PII prefill. (enterTerminal resets the lookup,
  // so this cannot loop.)
  useEffect(() => {
    if (lookupReady && readyPayloadUnusable) {
      enterTerminal({ kind: "unavailable", correlationId: lookupReady.correlationId });
    }
  }, [lookupReady, readyPayloadUnusable, enterTerminal]);

  // Support-phase derivation (U6): active on the dependency-unavailable
  // outcome itself, and kept active while the latched retry is pending.
  const lookupInSupport =
    lookup.data?.outcome === "dependency_unavailable" ||
    (lookupSupportCtx !== null && lookup.isPending);
  const completionInSupport =
    completion.data?.outcome === "dependency_unavailable" ||
    (completionSupportCtx !== null && completion.isPending);
  // A completion result that leaves Editing (completed, reconciliation,
  // already_completed, ...) is committed to TerminalState by the effect
  // above — one render later. That frame must not fall back to the editing
  // form: it would flash the form and move heading focus twice (form h1,
  // then the terminal h1). Hold a transitional phase until the effect lands.
  const completionTerminalBound =
    completion.data !== undefined &&
    completion.data.outcome !== "validation_failed" &&
    completion.data.outcome !== "dependency_unavailable" &&
    completion.data.outcome !== "rate_limited" &&
    completion.data.outcome !== "resubmit_required";
  const lookupRateLimited =
    lookup.data?.outcome === "rate_limited" || (lookupRateLimitCtx !== null && lookup.isPending);

  // One page-level phase per rendered state; a phase change moves focus to
  // the new page heading so keyboard and screen-reader users land on the
  // state transition (UI-contract accessibility: focus on heading / status).
  const phase = ((): string => {
    if (terminalState) {
      return `terminal:${terminalState.kind}`;
    }
    if (!hasProof) {
      return "terminal:unavailable";
    }
    if (lookupInSupport) {
      return "support:lookup";
    }
    if (lookupRateLimited) {
      return "rate-limited:lookup";
    }
    if (!lookup.data) {
      return "resolving";
    }
    if (!lookupReady || readyPayloadUnusable) {
      return "terminal:unavailable";
    }
    if (completionTerminalBound || memberColdRoutingBound) {
      return "transitioning";
    }
    if (completionInSupport) {
      return finalizeRetryPending ? "pending:finalize" : "support:completion";
    }
    return "editing";
  })();

  useEffect(() => {
    if (phase === "resolving" || phase === "transitioning") {
      // Loading/transitional states are announced via their role="status"
      // live region; the next phase (terminal) owns the heading focus move.
      return;
    }
    contentRef.current?.querySelector<HTMLElement>("h1")?.focus();
  }, [phase]);

  // Submit-level failures move focus to their Alert summary (UI contract).
  useEffect(() => {
    if (completion.data?.outcome === "validation_failed") {
      validationAlertRef.current?.focus();
    }
  }, [completion.data]);

  useEffect(() => {
    if (completion.isError) {
      uncertainAlertRef.current?.focus();
    }
  }, [completion.isError]);

  useEffect(() => {
    if (completion.data?.outcome === "rate_limited") {
      rateLimitAlertRef.current?.focus();
    }
  }, [completion.data]);

  useEffect(() => {
    if (completion.data?.outcome === "resubmit_required") {
      resubmitAlertRef.current?.focus();
    }
  }, [completion.data]);

  const handleSubmit = (
    values: VariantFormValues,
    clearedPaths: ReadonlySet<string>,
    password: string,
  ): void => {
    const proof = readProof();
    if (!proof || !lookupReady || !renderPlan || completion.isPending || completionSubmitHeld) {
      return;
    }
    const built = buildVariantCompletionRequest(
      lookupReady,
      proof,
      renderPlan,
      values,
      clearedPaths,
      password,
    );
    // The fingerprint excludes the proof, the password, and the completion
    // mode: an unchanged canonical form keeps the held key across a
    // resubmit_required re-entry (FR-050) or a rate-limit retry.
    const fingerprint = createCompletionFingerprint(built);
    const key = idempotencyKeys.keyFor(fingerprint);

    if (built.registrationVariant !== "member" || built.completionMode !== "initial") {
      lastSubmissionRef.current = {
        request: built,
        fingerprint,
        key,
        initialDispatched: false,
        mode: "initial",
        initialRecorded: false,
      };
      setFormDirty(false);
      completionMutate({ request: built, idempotencyKey: key });
      return;
    }

    // FR-050 member discipline (UI contract L153-L156): the password rides
    // ONLY on an `initial`; the same key's `initial` already dispatched means
    // this re-submit of the still-mounted form is a `finalize` (L154) —
    // unless the server said `resubmit_required`, in which case the
    // unchanged form is a NEW `initial` under the SAME key (L155), or the
    // `initial` was answered 429/503 before anything durable could exist, in
    // which case the re-entered password rides on a same-key `initial` again.
    const last = lastSubmissionRef.current;
    const resubmitRequired = resubmitRequiredRef.current;
    if (
      last !== null &&
      last.key === key &&
      last.initialDispatched &&
      last.initialRecorded &&
      !resubmitRequired
    ) {
      const finalize: MemberFinalizeCompletionRequest = {
        proof,
        registrationVariant: "member",
        completionMode: "finalize",
      };
      lastSubmissionRef.current = { ...last, mode: "finalize" };
      completionMutate({ request: finalize, idempotencyKey: key });
      return;
    }

    const { password: _password, ...envelope } = built;
    resubmitRequiredRef.current = false;
    pendingPasswordRef.current = password;
    lastSubmissionRef.current = {
      request: envelope,
      fingerprint,
      key,
      initialDispatched: true,
      mode: "initial",
      initialRecorded: true,
    };
    setFormDirty(false);
    completionMutate({
      request: envelope,
      idempotencyKey: key,
      takePassword: () => {
        // The handoff is single-use AND route-bound: once the page is
        // disposed nothing may read or dispatch the credential, even if
        // TanStack resumes a mutation it paused while offline.
        const taken = mountedRef.current ? pendingPasswordRef.current : null;
        pendingPasswordRef.current = null;
        return taken;
      },
    });
  };

  // 011-15 L156/L209: compare the live form against the last dispatched
  // submission on every business edit. The fingerprint strips proof,
  // password and completion mode, so a placeholder proof keeps the held
  // proof untouched while still yielding the canonical-form comparison.
  const handleValuesChange = (
    values: VariantFormValues,
    clearedPaths: ReadonlySet<string>,
  ): void => {
    const last = lastSubmissionRef.current;
    if (last === null || !lookupReady || !renderPlan) {
      setFormDirty(false);
      return;
    }
    const live = createCompletionFingerprint(
      buildVariantCompletionRequest(lookupReady, "", renderPlan, values, clearedPaths, ""),
    );
    setFormDirty(live !== last.fingerprint);
  };

  // Uncertain outcome or dependency retry: same unchanged canonical form,
  // same idempotency key (UI contract request behavior). No reset first —
  // the rendered panel stays mounted while the retry is pending (U6).
  // Member retries (FR-050) send the `finalize` body: the password went
  // over the wire once with the `initial` submission and is never re-sent;
  // the retained key ties the finalize to that initial. A form that has
  // diverged from that submission has no same-key retry (L156/L209): the
  // affordance is withdrawn and this guard backstops any stale activation.
  // Likewise a member `initial` answered 429/503 has nothing known to be
  // recorded, so no password-free `finalize` may be sent for it: the member
  // re-enters the password from the form instead.
  const retryLastSubmission = (): void => {
    const last = lastSubmissionRef.current;
    if (
      !last ||
      completion.isPending ||
      formDirty ||
      memberInitialUnrecorded(last, completion.data)
    ) {
      return;
    }
    let request: PasswordFreeCompletionRequest = last.request;
    if (last.request.registrationVariant === "member") {
      const proof = readProof();
      if (proof === null) {
        return;
      }
      const finalize: MemberFinalizeCompletionRequest = {
        proof,
        registrationVariant: "member",
        completionMode: "finalize",
      };
      request = finalize;
      lastSubmissionRef.current = { ...last, mode: "finalize" };
    }
    completionMutate({ request, idempotencyKey: last.key });
  };
  retryLastSubmissionRef.current = retryLastSubmission;

  // 011-21 FR-050 cold re-entry: the recovery-directed entry starts the
  // proof-bound credential recovery and hands off to the hosted sign-in with
  // the recovery reference; `not_available` (or any failure) leaves the
  // rendered guidance unchanged — never a credential CTA.
  const startCredentialRecovery = (): void => {
    const proof = readProof();
    if (proof === null || recoveryStarting) {
      return;
    }
    setRecoveryStarting(true);
    setRecoveryAnnouncement(null);
    void recoveryClient
      .start({ proof })
      .then((result) => {
        if (!mountedRef.current) {
          return;
        }
        if (
          result.outcome !== "started" ||
          result.recoveryReference === null ||
          !isCredentialRecoveryReference(result.recoveryReference)
        ) {
          // Visible guidance stays unchanged; only assistive tech hears that
          // the step ended, restating that same guidance.
          setRecoveryAnnouncement(RECOVERY_GUIDANCE_ANNOUNCEMENT);
          return;
        }
        const reference = result.recoveryReference;
        const returnTo = `/register/invitation/recovery?ref=${reference}`;
        const target = new URL("/auth/login", getRuntimeConfig().authBaseUrl);
        target.searchParams.set("recovery", reference);
        target.searchParams.set("returnTo", returnTo);
        window.location.assign(target.toString());
      })
      .catch(() => {
        if (mountedRef.current) {
          setRecoveryAnnouncement(RECOVERY_GUIDANCE_ANNOUNCEMENT);
        }
      })
      .finally(() => {
        if (mountedRef.current) {
          setRecoveryStarting(false);
        }
      });
  };

  // 011-24 brand continuity: ONLY the first-credential "set your password"
  // follow-up step (Surface 3 of the contract) renders on the same
  // clm-primary-blue backdrop as /set-password and the PublicAuthCard
  // interstitials. Non-follow-up completions and the registration FORM flow
  // keep the plain backdrop — including continue_financial_setup completions
  // whose FR-047 follow-up renders inside the financial flow's completed
  // surface, not as the standalone follow-up step.
  const brandedStep =
    ((terminalState?.kind === "completed" && terminalState.nextAction === "sign_in") ||
      terminalState?.kind === "already-completed") &&
    terminalState.firstCredentialFollowUp === true;

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/* FR-009.1: full public chrome (branded header + shared footer),
          matching PublicAuthCard (auth-routes.tsx). */}
      <PublicHeader />
      {/* FR-009.2: the branded follow-up step centers the max-w-lg card on the
          solid backdrop (PublicAuthCard composition); non-branded states keep
          the 800px column flow. */}
      <main
        className={cn(
          "clm-page-gutter flex-1",
          brandedStep
            ? "flex items-center justify-center bg-clm-primary-blue py-12"
            : "clm-section-spacing",
        )}
      >
        <div
          ref={contentRef}
          className={cn("mx-auto", brandedStep ? "w-full max-w-lg" : "max-w-[800px]")}
        >
          {renderPageState()}
        </div>
      </main>
      <AppFooter />
    </div>
  );

  function renderPageState(): ReactElement {
    if (terminalState) {
      return (
        <TerminalPanel
          state={terminalState}
          proofOwner={proofOwnerRef.current}
          recoveryEntry={
            <>
              <Button
                type="button"
                className="mt-4 w-full"
                onClick={startCredentialRecovery}
                disabled={recoveryStarting}
              >
                {recoveryStarting ? "Signing in…" : SIGN_IN_TO_CONTINUE_LABEL}
              </Button>
              {/* Polite live region: mounts empty; while the recovery start
                  is in flight it mirrors the button's pending label, and
                  afterwards only ever restates the guidance already on
                  screen (011-15 UI contract L172). */}
              <p role="status" aria-live="polite" className="sr-only">
                {recoveryStarting ? "Signing in…" : recoveryAnnouncement}
              </p>
            </>
          }
        />
      );
    }

    if (!hasProof) {
      // Missing/malformed fragment: generic unavailable, lookup never called.
      return <TerminalPanel state={{ kind: "unavailable" }} />;
    }

    if (lookupInSupport) {
      return (
        <SupportPanel
          correlationId={
            lookup.data?.outcome === "dependency_unavailable"
              ? lookup.data.correlationId
              : lookupSupportCtx?.correlationId
          }
          message="Registration is temporarily unavailable. Try again later."
          onRetry={() => {
            const proof = readProof();
            if (proof) {
              lookupMutate(proof);
            }
          }}
          retrying={lookup.isPending}
        />
      );
    }

    if (lookupRateLimited) {
      // US4: non-terminal wait-and-retry state, distinct from the generic
      // unavailable terminal. The proof is preserved so retrying can
      // proceed; the retry action honors the Retry-After window.
      return (
        <RateLimitPanel
          correlationId={
            lookup.data?.outcome === "rate_limited"
              ? lookup.data.correlationId
              : lookupRateLimitCtx?.correlationId
          }
          onRetry={() => {
            const proof = readProof();
            if (proof) {
              lookupMutate(proof);
            }
          }}
          retryHeld={lookupRetryHeld}
          retrying={lookup.isPending}
        />
      );
    }

    if (!lookup.data) {
      return (
        <div
          className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground"
          role="status"
        >
          <ClmIcon
            name="loading"
            decorative
            className="h-6 w-6 animate-spin text-clm-brand-blue motion-reduce:animate-none"
          />
          <span>Checking your registration link</span>
        </div>
      );
    }

    if (!lookupReady || !renderPlan) {
      // Covers non-ready unions (dependency_unavailable already returned via
      // lookupInSupport) and ready payloads carrying a retired/unknown
      // variant while their effects transition state; preserve correlation
      // context from the current lookup payload.
      return (
        <TerminalPanel state={{ kind: "unavailable", correlationId: lookup.data.correlationId }} />
      );
    }

    if (readyPayloadUnusable) {
      // Contract error: the server listed a reference path as editable but
      // served no catalog for it, or a member `ready` carries no usable
      // password policy. Same generic unavailable presentation as every
      // other unavailable outcome — no partial form, no detail. This branch
      // only covers the render before the terminal-scrub effect above
      // commits; the effect then owns the state (and the sensitive-data scrub).
      return (
        <TerminalPanel state={{ kind: "unavailable", correlationId: lookupReady.correlationId }} />
      );
    }

    if (completionTerminalBound || memberColdRoutingBound) {
      // The render before the terminal-transition effect commits: never the
      // editing form (no flash, no stray heading focus) — a quiet status.
      // Covers completion outcomes leaving Editing ("Finishing your
      // registration") and the cold member `ready` dispositions routed away
      // from Editing (011-21 addendum L60). The cold branch keeps the
      // completion-neutral resolving copy: nothing was submitted, and the
      // discriminator must not leak through the live region as a claim
      // about the registration's completion (UI contract 17a).
      return (
        <div
          className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground"
          role="status"
        >
          <ClmIcon
            name="loading"
            decorative
            className="h-6 w-6 animate-spin text-clm-brand-blue motion-reduce:animate-none"
          />
          <span>
            {completionTerminalBound
              ? "Finishing your registration"
              : "Checking your registration link"}
          </span>
        </div>
      );
    }

    const validationFailed =
      completion.data?.outcome === "validation_failed" ? completion.data : undefined;
    const rateLimited = completion.data?.outcome === "rate_limited" ? completion.data : undefined;
    const resubmitRequired = completion.data?.outcome === "resubmit_required";
    // FR-050 (L154/L158): the member `initial` for the CURRENT key already
    // carried the password and the SPA does not retain it, so the credential
    // group is "on file" until the server asks for it again (L155) or a
    // business edit moves the form to a new key (L156). A 422 creates no
    // durable attempt (L150): the corrected form is a NEW `initial`, so the
    // group re-opens on that render (the effect below nulls the ref after it).
    // A 429 or 503 answering the member `initial` likewise leaves nothing
    // known to be recorded: the group re-opens for password re-entry.
    const lastForRender = lastSubmissionRef.current;
    const initialUnrecorded = memberInitialUnrecorded(lastForRender, completion.data);
    const credentialsOnFile =
      lastForRender !== null &&
      lastForRender.request.registrationVariant === "member" &&
      lastForRender.initialDispatched &&
      !initialUnrecorded &&
      !resubmitRequiredRef.current &&
      !resubmitRequired &&
      validationFailed === undefined &&
      !formDirty;
    // 422 entries outside the rendered intersection are summarized in the
    // alert (server messages are public-safe) instead of being dropped. The
    // member `password` field is owned by the form's credential group (FR-050).
    const renderedPaths = renderedFieldPaths(renderPlan);
    const isMember = lookupReady.registrationVariant === "member";
    const unmappedFieldErrors = (validationFailed?.fieldErrors ?? []).filter(
      (fieldError) =>
        !renderedPaths.has(fieldError.field.replace(/^profile\./, "")) &&
        !(isMember && fieldError.field === "password"),
    );

    // UI contract L154/L168: a dependency_unavailable outcome keeps the form
    // MOUNTED ("your information was not lost") — the support surface (or the
    // Retry-After "still finishing" status) renders above it and owns the h1
    // for that phase so heading focus lands on the state transition.
    const supportSurface = completionInSupport ? (
      finalizeRetryPending ? (
        <FinalizePendingPanel />
      ) : (
        <SupportPanel
          correlationId={
            completion.data?.outcome === "dependency_unavailable"
              ? completion.data.correlationId
              : completionSupportCtx?.correlationId
          }
          message="Registration is temporarily unavailable. Try again later. Your information was not lost."
          onRetry={retryLastSubmission}
          retrying={completion.isPending}
          canRetry={!formDirty && !initialUnrecorded}
          resubmitMessage={
            initialUnrecorded && !formDirty
              ? "Re-enter your password below and choose Complete registration to try again."
              : changedDetailsMessage(isMember)
          }
        />
      )
    ) : null;

    return (
      <div>
        {supportSurface ?? (
          <div className="mb-6">
            <h1 tabIndex={-1} className="text-2xl font-bold text-clm-dark-gray focus:outline-none">
              {VARIANT_HEADINGS[lookupReady.registrationVariant]}
            </h1>
            <p className="mt-1 text-base text-gray-600">
              Review your details and complete your registration.
            </p>
          </div>
        )}

        {completion.isError && (
          <Alert ref={uncertainAlertRef} tabIndex={-1} variant="warning" className="mb-6">
            <AlertTitle>Your registration couldn&apos;t be confirmed</AlertTitle>
            <AlertDescription>
              <p>
                We didn&apos;t get a response. Nothing was lost — try again to submit the same
                registration.
              </p>
              {formDirty ? (
                <p className="mt-3">{changedDetailsMessage(isMember)}</p>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-3"
                  onClick={retryLastSubmission}
                  disabled={completion.isPending}
                >
                  Try again
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {rateLimited && (
          <Alert ref={rateLimitAlertRef} tabIndex={-1} variant="warning" className="mb-6">
            <AlertTitle>Too many attempts right now</AlertTitle>
            <AlertDescription>
              {initialUnrecorded ? RATE_LIMITED_REENTER_PASSWORD_MESSAGE : RATE_LIMITED_MESSAGE}
            </AlertDescription>
          </Alert>
        )}

        {resubmitRequired && (
          <Alert ref={resubmitAlertRef} tabIndex={-1} variant="warning" className="mb-6">
            <AlertTitle>Your password needs to be entered again</AlertTitle>
            <AlertDescription>Please re-enter your password and submit again.</AlertDescription>
          </Alert>
        )}

        {validationFailed && (
          <Alert ref={validationAlertRef} tabIndex={-1} variant="destructive" className="mb-6">
            <AlertTitle>Review the highlighted registration information</AlertTitle>
            <AlertDescription>
              <p>Correct the fields below and complete your registration again.</p>
              {unmappedFieldErrors.length > 0 && (
                <ul className="mt-2 list-inside list-disc">
                  {unmappedFieldErrors.map((fieldError) => (
                    <li key={`${fieldError.field}:${fieldError.code}`}>{fieldError.message}</li>
                  ))}
                </ul>
              )}
            </AlertDescription>
          </Alert>
        )}

        <VariantContextPanel lookup={lookupReady} plan={renderPlan} />
        <BoundCompanyPanel lookup={lookupReady} plan={renderPlan} />

        <RegistrationForm
          plan={renderPlan}
          prefill={lookupReady.prefill}
          expiresAt={lookupReady.expiresAt}
          submitting={completion.isPending || finalizeRetryPending}
          submitHeld={completionSubmitHeld}
          resubmitRequired={resubmitRequired}
          serverFieldErrors={validationFailed?.fieldErrors}
          passwordPolicy={
            lookupReady.registrationVariant === "member" ? lookupReady.passwordPolicy : undefined
          }
          credentialsOnFile={credentialsOnFile}
          onValuesChange={handleValuesChange}
          onSubmit={handleSubmit}
        />
      </div>
    );
  }
}

/**
 * Fail-closed page for legacy registration paths (FR-025 / T055): composite
 * `?key=` sections, reversible encrypted-ID routes, and other pre-rebuild
 * `/register/...` shapes render the same generic unavailable presentation —
 * public, with no auth redirect — and NEVER invoke lookup. Any fragment or
 * legacy query identifier is scrubbed on first render and discarded.
 */
export function RegistrationLegacyPathPage(): ReactElement {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/* FR-009.1: full public chrome (branded header + shared footer). */}
      <PublicHeader />
      <main className="clm-page-gutter clm-section-spacing flex-1">
        <RegistrationUnavailableContent />
      </main>
      <AppFooter />
    </div>
  );
}

/**
 * The page body of {@link RegistrationLegacyPathPage}, without its chrome, so
 * a route already inside the shared public layout (the CLMS-115 intake forms,
 * when intake is disabled in the stage — research D14) renders the same page
 * body without a second header, footer or `main`.
 */
export function RegistrationUnavailableContent(): ReactElement {
  // Render-time scrub, mirroring the admin-realm page: capture (which
  // removes the fragment and legacy parameters) then discard immediately.
  // Ticket-scoped: scrubs exactly what THIS render staged (a null ticket
  // resets only a proof-less memoization) without erasing a capture staged
  // by a coexisting public flow.
  useState(() => {
    const capture = captureInvitationProofFromLocation();
    clearCapturedInvitationProof(capture.ticket);
    return null;
  });

  return (
    <div className="mx-auto max-w-[800px]">
      <TerminalPanel state={{ kind: "unavailable" }} />
    </div>
  );
}

function mapPublicOutcomeToTerminal(
  result: {
    outcome: string;
    correlationId: string;
  },
  firstCredentialFollowUp: boolean,
): TerminalState {
  switch (result.outcome) {
    case "expired":
      return { kind: "expired", correlationId: result.correlationId };
    case "already_completed":
      // Durable-notice re-entry: the follow-up latch keeps the
      // first-credential status surface mounted; the proof itself stays in
      // the ownership-scoped holder (eligibility is checked by the caller).
      return {
        kind: "already-completed",
        correlationId: result.correlationId,
        ...(firstCredentialFollowUp && { firstCredentialFollowUp: true }),
      };
    case "reconciliation_required":
      return {
        kind: "support",
        correlationId: result.correlationId,
        message: "Our team needs to review this registration before it can be completed.",
      };
    default:
      // unavailable, rate_limited, invalid request, idempotency conflict:
      // one generic unavailable presentation, no eligibility detail.
      return { kind: "unavailable", correlationId: result.correlationId };
  }
}

/**
 * 011-24 shared card/brand chrome for the first-credential "set your
 * password" follow-up step: the same white card + shared brand header
 * composition as /set-password (and PublicAuthCard), so the CTA handoff into
 * password setup reads as one continuous branded surface. Applied only when
 * the follow-up latch is set — never to non-follow-up completions or the
 * registration form (contract Surface 3 scope).
 */
function FollowUpBrandCard({ children }: { children: ReactNode }): ReactElement {
  return (
    <Card className="mx-auto w-full max-w-lg border-0 bg-white px-2 py-8 shadow-md sm:px-8">
      <div className="px-6">
        <PublicBrandHeader className="mb-8" />
        {children}
      </div>
    </Card>
  );
}

function TerminalPanel({
  state,
  proofOwner = null,
  recoveryEntry = null,
}: {
  state: TerminalState;
  /** Holder ownership token for the follow-up surface (non-secret). */
  proofOwner?: FirstCredentialProofOwner | null;
  /** 011-21 FR-050 recovery-directed entry for the follow-up's `no_action`
   * guidance on cold re-entry (never a credential CTA). */
  recoveryEntry?: ReactNode;
}): ReactElement {
  // No aria-live wrappers: these panels mount WITH their content, so a live
  // region here never announces (U5). The page-level focus move to the new
  // h1 is the announcement mechanism for terminal transitions.
  switch (state.kind) {
    case "completed": {
      const memberCompletion = state.registrationVariant === "member";
      const panel = (
        <section>
          <h1
            tabIndex={-1}
            className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
          >
            Registration complete
          </h1>
          {/* role="status": success confirmation, not an alert (U5). */}
          <Alert role="status" variant="success" className="mt-4">
            <AlertTitle>Your registration was completed successfully</AlertTitle>
            {/* In first-credential follow-up mode the supporting copy is
                owned by the resolved status action below — keyed on the
                stable non-secret latch, NOT the scrubbable proof, so the
                unsafe bare "You can now sign in" sentence can never
                reappear when the secret is cleared on a failure path.
                Member completions (FR-050) carry the warm sign-in copy: the
                password was created in this flow, so no status step runs. */}
            {memberCompletion ? (
              <AlertDescription>
                Registration complete. You can sign in with your email and the password you just
                created.
                {state.nextAction === "continue_financial_setup" && (
                  <span className="mt-1 block">
                    {completionNextActionCopy(
                      state.nextAction,
                      state.inlineFinancialAuthority === true,
                    ).replace(/^Your registration is complete\. /, "")}
                  </span>
                )}
              </AlertDescription>
            ) : (
              !(state.nextAction === "sign_in" && state.firstCredentialFollowUp) && (
                <AlertDescription>
                  {completionNextActionCopy(
                    state.nextAction,
                    state.inlineFinancialAuthority === true,
                  )}
                </AlertDescription>
              )
            )}
          </Alert>
          {!memberCompletion &&
            state.nextAction === "sign_in" &&
            (state.firstCredentialFollowUp ? (
              <FirstCredentialFollowUp proofOwner={proofOwner} />
            ) : (
              <Button asChild className="mt-4 w-full">
                <Link to="/login">Sign in</Link>
              </Button>
            ))}
          {state.nextAction === "continue_financial_setup" && (
            <>
              {/* 006-6 UI contract: warm MemberCompletionSucceeded held by this
                  mount IS the credential evidence; non-member completions carry
                  no member credential, so the financial result never claims one. */}
              <RegistrationFinancialOutcome
                handoff={state.financialHandoff}
                signInProven={memberCompletion}
              />
              {/* Keep the payment and registration references grouped, above the secondary Sign in link. */}
              <SupportReference
                label="Registration reference"
                correlationId={state.correlationId}
              />
            </>
          )}
          {memberCompletion && (
            // 011-15 UI contract L164: the pending financial status is the
            // primary action; "Sign in" is the secondary link beneath it.
            <Button
              asChild
              variant={state.nextAction === "continue_financial_setup" ? "secondary" : "default"}
              className="mt-4 w-full"
            >
              <Link to="/login">Sign in</Link>
            </Button>
          )}
          {state.nextAction !== "continue_financial_setup" && (
            <SupportReference label="Registration reference" correlationId={state.correlationId} />
          )}
        </section>
      );
      // 011-24 Surface 3 scope: brand chrome only on the standalone
      // first-credential follow-up step (sign_in completions); financial-
      // handoff completions and other panels render the pre-feature chrome.
      return state.firstCredentialFollowUp && state.nextAction === "sign_in" ? (
        <FollowUpBrandCard>{panel}</FollowUpBrandCard>
      ) : (
        panel
      );
    }
    case "expired":
      return (
        <section>
          <h1
            tabIndex={-1}
            className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
          >
            This registration link has expired
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Registration links are valid for a limited time. Contact support to request a new
            registration link.
          </p>
          <SupportReference label="Registration reference" correlationId={state.correlationId} />
        </section>
      );
    case "already-completed": {
      const panel = (
        <section>
          <h1
            tabIndex={-1}
            className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
          >
            This registration is already complete
          </h1>
          <p className="mt-2 text-base text-gray-600">
            This registration is already complete.
            {/* The follow-up owns the sign-in guidance in first-credential
                mode; repeating "Sign in to continue" here would duplicate
                its recovery entry (011-15 UI contract L167, L172). */}
            {!state.firstCredentialFollowUp &&
              " Sign in to continue. Contact support if you need help with your account."}
          </p>
          {state.firstCredentialFollowUp ? (
            <FirstCredentialFollowUp
              proofOwner={proofOwner}
              recoveryEntry={recoveryEntry}
              memberContext={state.memberContext === true}
            />
          ) : (
            <Button asChild className="mt-4 w-full">
              <Link to="/login">Sign in</Link>
            </Button>
          )}
          <SupportReference label="Registration reference" correlationId={state.correlationId} />
        </section>
      );
      // 011-24 Surface 3 scope: brand chrome only on the first-credential
      // follow-up step; other completions render the pre-feature panel.
      return state.firstCredentialFollowUp ? <FollowUpBrandCard>{panel}</FollowUpBrandCard> : panel;
    }
    case "recovery-directed":
      // 011-21 addendum L60 (clm-web#238 V5) as amended by UI contract 17a:
      // cold member `ready` with `recovery_available`. Completion-NEUTRAL
      // heading — the lookup disposition is not a claim that this
      // registration is complete, so the already-completed copy never
      // renders here. The guidance row and the A §8.1 recovery entry render
      // byte-identical to the cold `no_action` follow-up row (shared
      // RECOVERY_DIRECTED_GUIDANCE literal, same caller-provided entry)
      // WITHOUT mounting FirstCredentialFollowUp: the lookup already
      // resolved the disposition, so no status request is made. Like the
      // other lookup-phase terminals it renders on the plain backdrop — 011-24
      // Surface 3 scopes brand chrome to FirstCredentialFollowUp panels.
      return (
        <section>
          <h1
            tabIndex={-1}
            className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
          >
            {SIGN_IN_TO_CONTINUE_LABEL}
          </h1>
          <div className="mt-4">
            <div>
              <p className="text-base text-gray-600">{RECOVERY_DIRECTED_GUIDANCE}</p>
              {recoveryEntry}
            </div>
          </div>
          <SupportReference label="Registration reference" correlationId={state.correlationId} />
        </section>
      );
    case "registration-paused":
      return (
        <section>
          <h1
            tabIndex={-1}
            className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
          >
            {REGISTRATION_PAUSED_HEADING}
          </h1>
          <p className="mt-2 text-base text-gray-600">{REGISTRATION_PAUSED_MESSAGE}</p>
        </section>
      );
    case "support":
      return (
        <section>
          <h1
            tabIndex={-1}
            className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
          >
            {state.heading ?? "We can't complete your registration right now"}
          </h1>
          <p className="mt-2 text-base text-gray-600">
            {state.message ?? "Contact support for help with this registration."}{" "}
            {state.signInEntry
              ? "If you have questions, contact support and share the reference below."
              : "Contact support and share the reference below."}
          </p>
          {state.signInEntry && (
            <Button asChild className="mt-4 w-full">
              <Link to="/login">Sign in</Link>
            </Button>
          )}
          <SupportReference label="Registration reference" correlationId={state.correlationId} />
        </section>
      );
    default:
      return (
        <section>
          <h1
            tabIndex={-1}
            className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
          >
            This registration link is unavailable
          </h1>
          <p className="mt-2 text-base text-gray-600">
            The link may be incorrect or no longer active. Open the link from your registration
            email again to retry, or contact support if you need a new registration link.
          </p>
          <SupportReference label="Registration reference" correlationId={state.correlationId} />
        </section>
      );
  }
}

function SupportPanel({
  correlationId,
  message,
  onRetry,
  retrying,
  canRetry = true,
  resubmitMessage = CHANGED_DETAILS_MESSAGE,
}: {
  correlationId?: string;
  message: string;
  onRetry: () => void;
  retrying: boolean;
  /** False once the form diverged from the failed submission (011-15
   * L156/L209): the same-key retry no longer describes the form, so the
   * affordance is withdrawn and the member resubmits from the form. */
  canRetry?: boolean;
  /** Guidance shown instead of "Try again" when `canRetry` is false. */
  resubmitMessage?: string;
}): ReactElement {
  const [retryAttempted, setRetryAttempted] = useState(false);
  return (
    <section>
      <h1
        tabIndex={-1}
        className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
      >
        Registration is temporarily unavailable
      </h1>
      <p className="mt-2 text-base text-gray-600">{message}</p>
      {/* A failed retry re-renders this same panel (U6 latch), so without a
          live region assistive technology gets no feedback that the retry
          ran and registration is still unavailable (WCAG 4.1.3). */}
      <p role="status" className="mt-2 text-sm text-gray-600">
        {retrying
          ? "Retrying\u2026"
          : retryAttempted
            ? "Registration is still unavailable. Try again later."
            : null}
      </p>
      {canRetry ? (
        <Button
          type="button"
          variant="outline"
          className="mt-4"
          onClick={() => {
            setRetryAttempted(true);
            onRetry();
          }}
          disabled={retrying}
        >
          Try again
        </Button>
      ) : (
        <p className="mt-4 text-base text-gray-600">{resubmitMessage}</p>
      )}
      <SupportReference label="Registration reference" correlationId={correlationId} />
    </section>
  );
}

/**
 * UI contract L168: a member `finalize` answered 503 with Retry-After stays
 * in the Submitting (pending) state — no manual retry — while the SPA waits
 * out the interval and re-sends `finalize` under the same key.
 */
function FinalizePendingPanel(): ReactElement {
  return (
    <section className="mb-6">
      <h1
        tabIndex={-1}
        className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
      >
        Finishing your registration
      </h1>
      <p role="status" className="mt-2 flex items-center gap-2 text-base text-gray-600">
        <ClmIcon
          name="loading"
          decorative
          className="h-4 w-4 animate-spin text-clm-brand-blue motion-reduce:animate-none"
        />
        Still finishing your registration&hellip;
      </p>
    </section>
  );
}

function RateLimitPanel({
  correlationId,
  onRetry,
  retryHeld,
  retrying,
}: {
  correlationId?: string;
  onRetry: () => void;
  retryHeld: boolean;
  retrying: boolean;
}): ReactElement {
  const [retryAttempted, setRetryAttempted] = useState(false);
  // Generic, non-technical copy: no threshold, window, or eligibility
  // detail. The disabled retry (Retry-After hold) is conveyed by the button
  // state plus the wait guidance text — never a countdown (UI contract).
  return (
    <section>
      <h1
        tabIndex={-1}
        className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
      >
        Too many attempts right now
      </h1>
      <p className="mt-2 text-base text-gray-600">
        Please wait a moment, then try your registration link again.
      </p>
      {/* Same announcement gap as SupportPanel: a retry that is rate limited
          again re-renders this same panel, so a polite region is the only
          feedback that the retry ran (WCAG 4.1.3). */}
      <p role="status" className="mt-2 text-sm text-gray-600">
        {retrying
          ? "Retrying\u2026"
          : retryAttempted
            ? "Still too many attempts. Please wait a moment before trying again."
            : null}
      </p>
      <Button
        type="button"
        variant="outline"
        className="mt-4"
        onClick={() => {
          setRetryAttempted(true);
          onRetry();
        }}
        disabled={retryHeld || retrying}
      >
        Try again
      </Button>
      <SupportReference label="Registration reference" correlationId={correlationId} />
    </section>
  );
}

function completionNextActionCopy(
  nextAction: CompletionSucceeded["nextAction"] | undefined,
  inlineFinancialAuthority: boolean,
): string {
  switch (nextAction) {
    case "continue_financial_setup":
      // With payment-step authority the financial flow renders directly
      // below — pointing elsewhere ("instructions you receive") would
      // contradict it. Directional-only copy: the flow's own surface states
      // whether a payment, a wait, or nothing is due.
      return inlineFinancialAuthority
        ? "Your registration is complete. Finish setting up your membership below."
        : "Your registration is complete. Financial setup continues separately; follow the instructions you receive to finish setting up your membership.";
    default:
      return "You can now sign in with your registered email.";
  }
}

/**
 * Member pending-financial-setup completion context: the CLMS-117
 * status-driven surface (payment step / result states) is owned by
 * RegistrationFinancialOutcome in registration-financial-outcome.tsx. The
 * SPA still never constructs provider, payment, encrypted-person-ID, or
 * handoff routes.
 */
